/**
 * Reparatie Cargo Section
 *
 * Rendert per reparateur-stop op een route drie heldere secties met cargo:
 *  1. Brengen — kleden die deze rit naar de reparateur worden afgeleverd
 *  2. Ophalen — kleden die deze rit bij de reparateur worden opgehaald
 *  3. Liggen er nog — kleden die op dit moment bij de reparateur zijn
 *
 * Functies:
 *  - Per-WI Afgeleverd/Opgehaald knoppen
 *  - Bulk-knoppen "Alles afgeleverd" / "Alles opgehaald"
 *  - Navigeer-knop + Bel-knop (zelfde patroon als StopCard)
 *  - "Stop afronden" knop (markeert Geplande_Rit__c.Status='Verwerkt')
 */

import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { WbButton, WbChip, WbFlag, WbListRow, WbSectionKop } from '../wb';
import { salesforceClient } from '../../lib/salesforce';
import type {
  GeplandeRitWithDetails,
  ReparatieCargoDTO,
  ReparatieCargoItem,
} from '../../lib/salesforce/types';

interface Props {
  stop: GeplandeRitWithDetails;
  isNext: boolean;
  onComplete: () => void;
  onNavigate: () => void;
  onCall: () => void;
  onCargoChanged?: () => void;
  /**
   * Embedded = gerenderd naast een gewone StopCard op een gemengde stop
   * (klantorder op een reparateur-account). Verbergt bel/navigeer en de
   * "Stop afronden"-knop — die horen bij de StopCard; hier alleen de
   * reparatie-kleden met hun eigen afgeleverd/opgehaald-acties.
   */
  embedded?: boolean;
}

export function ReparatieCargoSection({
  stop,
  isNext,
  onComplete,
  onNavigate,
  onCall,
  onCargoChanged,
  embedded = false,
}: Props) {
  const [cargo, setCargo] = useState<ReparatieCargoDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyWiId, setBusyWiId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState<'heen' | 'terug' | null>(null);

  const fetchCargo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await salesforceClient.getReparatieCargo(stop.Id);
      if (!res.success || !res.data) {
        setError(res.message || 'Kon cargo niet laden');
        return;
      }
      setCargo({
        geplandeRitId: res.data.geplandeRitId ?? null,
        reparateurId: res.data.reparateurId ?? null,
        reparateurNaam: res.data.reparateurNaam ?? null,
        heen: (res.data.heen as unknown as ReparatieCargoItem[]) ?? [],
        terug: (res.data.terug as unknown as ReparatieCargoItem[]) ?? [],
        liggenErNog: (res.data.liggenErNog as unknown as ReparatieCargoItem[]) ?? [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cargo laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [stop.Id]);

  useEffect(() => {
    fetchCargo();
  }, [fetchCargo]);

  const markOne = useCallback(
    async (wiId: string, action: 'afgeleverd' | 'opgehaald') => {
      const res =
        action === 'afgeleverd'
          ? await salesforceClient.markReparatieKleedAfgeleverd(wiId)
          : await salesforceClient.markReparatieKleedOpgehaald(wiId);
      if (!res.success) {
        throw new Error(res.message || `Markeren ${action} mislukt`);
      }
    },
    []
  );

  const handleMark = useCallback(
    async (wiId: string, action: 'afgeleverd' | 'opgehaald') => {
      setBusyWiId(wiId);
      setError(null);
      try {
        await markOne(wiId, action);
        if (navigator.vibrate) navigator.vibrate(50);
        await fetchCargo();
        onCargoChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : `Markeren ${action} mislukt`);
      } finally {
        setBusyWiId(null);
      }
    },
    [markOne, fetchCargo, onCargoChanged]
  );

  const handleBulk = useCallback(
    async (action: 'afgeleverd' | 'opgehaald') => {
      if (!cargo) return;
      const items = action === 'afgeleverd' ? cargo.heen : cargo.terug;
      const remaining = items.filter(
        (it) =>
          action === 'afgeleverd'
            ? !it.reparatieAfgeleverdOp
            : !it.reparatieOpgehaaldOp
      );
      if (remaining.length === 0) return;
      setBulkBusy(action === 'afgeleverd' ? 'heen' : 'terug');
      setError(null);
      try {
        for (const it of remaining) {
          await markOne(it.wiId, action);
        }
        if (navigator.vibrate) navigator.vibrate(50);
        await fetchCargo();
        onCargoChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : `Bulk ${action} mislukt`);
      } finally {
        setBulkBusy(null);
      }
    },
    [cargo, markOne, fetchCargo, onCargoChanged]
  );

  const heenRemaining = cargo
    ? cargo.heen.filter((it) => !it.reparatieAfgeleverdOp).length
    : 0;
  const terugRemaining = cargo
    ? cargo.terug.filter((it) => !it.reparatieOpgehaaldOp).length
    : 0;

  const [completing, setCompleting] = useState(false);
  const handleCompleteAll = useCallback(async () => {
    if (!cargo) {
      onComplete();
      return;
    }
    setCompleting(true);
    setError(null);
    try {
      // Mark alle nog-niet-afgeleverde heen-cargo
      const heenTodo = cargo.heen.filter((it) => !it.reparatieAfgeleverdOp);
      for (const it of heenTodo) {
        await markOne(it.wiId, 'afgeleverd');
      }
      // Mark alle nog-niet-opgehaalde terug-cargo
      const terugTodo = cargo.terug.filter((it) => !it.reparatieOpgehaaldOp);
      for (const it of terugTodo) {
        await markOne(it.wiId, 'opgehaald');
      }
      if (navigator.vibrate) navigator.vibrate(50);
      // Daarna stop afronden + naar volgende stop
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Stop afronden mislukt');
      setCompleting(false);
    }
  }, [cargo, markOne, onComplete]);

  const totaalTodo = heenRemaining + terugRemaining;

  return (
    <div className={clsx('rounded-wb-lg border-wb bg-wb-vlak overflow-hidden', isNext ? 'border-wb-mint' : 'border-wb-rand')}>
      {/* Kop: reparateur, adres, tijdvenster; embedded = onder een klantorder op hetzelfde adres */}
      <div className="px-3 py-2 border-b-wb border-wb-rand flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <WbChip kind="neutraal">Reparatie</WbChip>
            {!embedded && stop.timeWindow && <span className="font-mono text-sm font-semibold text-wb-inkt">{stop.timeWindow}</span>}
          </div>
          <p className="font-wb-heading font-bold text-xl leading-tight text-wb-inkt truncate mt-0.5">{cargo?.reparateurNaam || stop.customerName || 'Reparateur'}</p>
          {embedded ? (
            <p className="text-sm text-wb-inkt2">Reparatie-kleden op dit adres, los van de klantorder hierboven</p>
          ) : (
            stop.address && <p className="text-sm text-wb-inkt2 whitespace-pre-line">{stop.address}</p>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {!embedded && (
          <div className="grid grid-cols-2 gap-2">
            <WbButton variant="secondary" onClick={onNavigate}>Navigeer</WbButton>
            <WbButton variant="secondary" onClick={onCall} disabled={!stop.phoneNumber}>Bel</WbButton>
          </div>
        )}

        {loading && <p className="font-mono text-sm text-wb-inkt2">Lading laden…</p>}
        {error && <WbFlag kind="geld">{error}</WbFlag>}

        {!loading && cargo && (
          <>
            <CargoBlock
              title="Brengen naar reparateur"
              subtitle="Kleden die je hier bij de reparateur aflevert"
              items={cargo.heen}
              busyWiId={busyWiId}
              bulkBusy={bulkBusy === 'heen'}
              actionLabel="Afgeleverd"
              onAction={(wiId) => handleMark(wiId, 'afgeleverd')}
              onBulk={heenRemaining > 1 ? () => handleBulk('afgeleverd') : null}
              bulkLabel={heenRemaining > 1 ? `Alles afgeleverd (${heenRemaining})` : null}
              alreadyDoneCheck={(it) => !!it.reparatieAfgeleverdOp}
              alreadyDoneLabel="Afgeleverd bij reparateur"
            />
            <CargoBlock
              title="Ophalen bij reparateur"
              subtitle="Gerepareerde kleden die je hier meeneemt"
              items={cargo.terug}
              busyWiId={busyWiId}
              bulkBusy={bulkBusy === 'terug'}
              actionLabel="Opgehaald"
              onAction={(wiId) => handleMark(wiId, 'opgehaald')}
              onBulk={terugRemaining > 1 ? () => handleBulk('opgehaald') : null}
              bulkLabel={terugRemaining > 1 ? `Alles opgehaald (${terugRemaining})` : null}
              alreadyDoneCheck={(it) => !!it.reparatieOpgehaaldOp}
              alreadyDoneLabel="Opgehaald bij reparateur"
            />
            <CargoBlock
              title="Liggen er nog"
              subtitle="Wat nu bij de reparateur ligt, geen actie nodig"
              items={cargo.liggenErNog}
              busyWiId={null}
              bulkBusy={false}
              actionLabel={null}
              onAction={null}
              onBulk={null}
              bulkLabel={null}
              alreadyDoneCheck={() => false}
              alreadyDoneLabel=""
              compact
            />
          </>
        )}

        {!loading && cargo && !embedded && (
          <WbButton size="lg" onClick={handleCompleteAll} disabled={bulkBusy !== null || busyWiId !== null} isLoading={completing}>
            {totaalTodo > 0 ? `Alles klaar (${totaalTodo}) · volgende` : 'Stop afronden · volgende'}
          </WbButton>
        )}
      </div>
    </div>
  );
}

interface CargoBlockProps {
  title: string;
  subtitle?: string;
  items: ReparatieCargoItem[];
  busyWiId: string | null;
  bulkBusy: boolean;
  actionLabel: string | null;
  onAction: ((wiId: string) => void) | null;
  onBulk: (() => void) | null;
  bulkLabel: string | null;
  alreadyDoneCheck: (item: ReparatieCargoItem) => boolean;
  alreadyDoneLabel: string;
  compact?: boolean;
}

function CargoBlock({
  title,
  subtitle,
  items,
  busyWiId,
  bulkBusy,
  actionLabel,
  onAction,
  onBulk,
  bulkLabel,
  alreadyDoneCheck,
  alreadyDoneLabel,
  compact,
}: CargoBlockProps) {
  return (
    <section className="space-y-1.5">
      <WbSectionKop
        label={title}
        teller={items.length}
        uitleg={subtitle}
        niveau={3}
        actie={onBulk && bulkLabel ? (
          <WbButton variant="link" onClick={onBulk} disabled={bulkBusy}>{bulkBusy ? 'Bezig…' : bulkLabel}</WbButton>
        ) : undefined}
      />
      {items.length === 0 ? (
        <p className="text-sm text-wb-inkt2">Geen kleden</p>
      ) : (
        items.map((item) => {
          const done = alreadyDoneCheck(item);
          const busy = busyWiId === item.wiId;
          return (
            <WbListRow
              key={item.wiId}
              hoofd={item.wiNaam}
              klaar={done}
              className={clsx(compact && 'opacity-80')}
              chips={item.afmetingen ? <WbChip kind="neutraal">{item.afmetingen}</WbChip> : undefined}
              sub={
                <>
                  {item.opdrachtTekst && <span className="block font-body text-wb-inkt whitespace-normal">{item.opdrachtTekst}</span>}
                  {[item.klantNaam ? `Klant ${item.klantNaam}` : null, item.opdrachtNummer].filter(Boolean).join(' · ')}
                </>
              }
              links={item.fotoUrl ? <img src={item.fotoUrl} alt={item.wiNaam} className="w-12 h-12 rounded-wb object-cover border-wb border-wb-rand" /> : undefined}
              rechts={
                actionLabel && onAction && !done ? (
                  <WbButton fullWidth={false} onClick={() => onAction(item.wiId)} disabled={busy || bulkBusy} isLoading={busy}>{actionLabel}</WbButton>
                ) : done ? (
                  <WbChip kind="klaar">{alreadyDoneLabel}</WbChip>
                ) : null
              }
            />
          );
        })
      )}
    </section>
  );
}
